import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { API_BASE } from '../utils/api'

interface EventDate {
  date: string
  name: string
  type: string
}

export function HolidayCalendarSidebar({ onClose }: { onClose: () => void }) {
  const [events, setEvents] = useState<EventDate[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  
  // Form state
  const [newDate, setNewDate] = useState('')
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState('holiday') // holiday, event, expiry
  
  const { token } = useAuth()
  const { addToast } = useToast()

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setEvents(data)
      } else {
        addToast('Failed to fetch calendar', 'error')
      }
    } catch (err) {
      addToast('Network error fetching calendar', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [token])

  const handleAdd = async () => {
    if (!newDate || !newName) {
      addToast('Date and Name are required', 'warning')
      return
    }

    setAdding(true)
    try {
      const res = await fetch(`${API_BASE}/api/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: newDate,
          name: newName,
          type: newType
        })
      })

      if (res.ok) {
        addToast('Event added successfully', 'success')
        setNewDate('')
        setNewName('')
        fetchEvents()
      } else {
        const err = await res.json()
        addToast(err?.error || 'Failed to add event', 'error')
      }
    } catch (err) {
      addToast('Network error adding event', 'error')
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (date: string) => {
    if (!confirm(`Are you sure you want to delete the event on ${date}?`)) return
    
    try {
      const res = await fetch(`${API_BASE}/api/events/${date}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (res.ok) {
        addToast('Event deleted', 'success')
        fetchEvents()
      } else {
        addToast('Failed to delete event', 'error')
      }
    } catch (err) {
      addToast('Network error deleting event', 'error')
    }
  }

  // Group events by month
  const groupedEvents = events.reduce((acc, event) => {
    const d = new Date(event.date)
    const month = d.toLocaleString('en-IN', { month: 'long', year: 'numeric' })
    if (!acc[month]) acc[month] = []
    acc[month].push(event)
    return acc
  }, {} as Record<string, EventDate[]>)

  return (
    <div className="strat-sidebar-overlay" onClick={onClose}>
      <div className="strat-sidebar-panel" onClick={e => e.stopPropagation()}>
        <div className="strat-sidebar-header">
          <div>
            <h3>🗓 Holiday & Event Calendar</h3>
            <p>Manage global non-trading days.</p>
          </div>
          <button className="strat-sidebar-close" onClick={onClose}>✕</button>
        </div>

        <div className="strat-sidebar-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1.5rem' }}>
          
          {/* Add New Event */}
          <div>
            <h4 style={{ fontSize: '0.875rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>+ Add No-Trade Day</h4>
            <div className="cfg-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="cfg-field">
                <label className="cfg-label">Event Name</label>
                <input 
                  type="text" 
                  className="cfg-input" 
                  placeholder="e.g. Diwali" 
                  value={newName} 
                  onChange={e => setNewName(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="cfg-field" style={{ flex: 1 }}>
                  <label className="cfg-label">Date</label>
                  <input 
                    type="date" 
                    className="cfg-input" 
                    value={newDate} 
                    onChange={e => setNewDate(e.target.value)} 
                  />
                </div>
                <div className="cfg-field" style={{ flex: 1 }}>
                  <label className="cfg-label">Type</label>
                  <select className="cfg-input" value={newType} onChange={e => setNewType(e.target.value)}>
                    <option value="holiday">Exchange Holiday</option>
                    <option value="event">Major Event</option>
                    <option value="expiry">Special Expiry</option>
                  </select>
                </div>
              </div>
            </div>
            <button 
              className="btn btn-primary btn-sm" 
              style={{ marginTop: '1rem', width: '100%' }} 
              onClick={handleAdd}
              disabled={adding}
            >
              {adding ? 'Adding...' : 'Add to Calendar'}
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--border)' }} />

          {/* List Events */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>Calendar Events</h4>
              <span style={{ fontSize: '0.65rem', background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '0.2rem 0.5rem', borderRadius: '4px', border: '1px solid rgba(16,185,129,0.2)' }}>
                Source: NSE API (Auto-synced)
              </span>
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                Loading calendar...
              </div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
                No events or holidays found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {Object.entries(groupedEvents).map(([month, monthEvents]) => (
                  <div key={month}>
                    <h5 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--border)' }}>
                      {month}
                    </h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {monthEvents.map(event => (
                        <div key={event.date} style={{ 
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '0.75rem 1rem', background: 'var(--bg-elevated)', 
                          borderRadius: '6px', border: '1px solid var(--border)'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>{event.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.25rem' }}>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{event.date}</span>
                              <span style={{
                                fontSize: '0.625rem', padding: '0.1rem 0.4rem', borderRadius: '3px', textTransform: 'uppercase', fontWeight: 600,
                                background: event.type === 'holiday' ? 'rgba(244,63,94,0.1)' : 'rgba(234,179,8,0.1)',
                                color: event.type === 'holiday' ? '#f43f5e' : '#eab308',
                              }}>
                                {event.type}
                              </span>
                            </div>
                          </div>
                          <button 
                            style={{ 
                              background: 'transparent', border: 'none', color: 'var(--text-muted)', 
                              cursor: 'pointer', padding: '0.5rem', borderRadius: '4px', fontSize: '1rem' 
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#f43f5e'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                            onClick={() => handleDelete(event.date)}
                            title="Remove event"
                          >
                            🗑
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
